import type { JSX } from "react";

/** Names of the available icons. */
export type IconName =
  | "clock"
  | "times"
  | "balances"
  | "corrections"
  | "absences"
  | "team"
  | "violations"
  | "projects"
  | "setup"
  | "admin"
  | "tenant"
  | "billing"
  | "privacy"
  | "install"
  | "more"
  | "chevronRight"
  | "chevronLeft"
  | "account"
  | "chevronDown"
  | "chevronUp"
  | "settings"
  | "signOut"
  | "roster"
  | "rosterPlanning"
  | "pause"
  | "warning";

/**
 * Stroke paths on a 24 × 24 grid, drawn for this application (no icon font, no dependency, no
 * external request under the content security policy).
 */
const iconPaths: Readonly<Record<IconName, readonly string[]>> = {
  clock: ["M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18", "M12 7v5l3 2"],
  times: ["M4 5h16v15H4z", "M4 9h16", "M8 3v4", "M16 3v4", "M8 13h8", "M8 16h5"],
  balances: ["M4 20h16", "M6 20v-6", "M10 20V9", "M14 20v-9", "M18 20V5"],
  corrections: ["M4 20h4L19 9l-4-4L4 16z", "M13 7l4 4"],
  absences: ["M4 5h16v15H4z", "M4 9h16", "M8 3v4", "M16 3v4", "M9 14l2 2l4-4"],
  team: [
    "M9 11a3 3 0 1 0 0-6a3 3 0 1 0 0 6",
    "M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1",
    "M16 5a3 3 0 0 1 0 6",
    "M18 14a4 4 0 0 1 3 4v2",
  ],
  violations: ["M12 4L3 20h18z", "M12 10v4", "M12 17v.5"],
  setup: ["M9 6h11", "M9 12h11", "M9 18h11", "M4 6l1 1l2-2", "M4 12l1 1l2-2", "M4 18l1 1l2-2"],
  projects: ["M3 7h7l2 2h9v10H3z", "M3 7V5h6l2 2"],
  admin: ["M4 7h10", "M18 7h2", "M4 17h4", "M12 17h8", "M14 5v4", "M8 15v4"],
  tenant: ["M5 20V5l7-2v17", "M12 9h7v11", "M3 20h18", "M8 8v.5", "M8 12v.5", "M8 16v.5"],
  billing: ["M6 3h12v18l-3-2l-3 2l-3-2l-3 2z", "M9 8h6", "M9 12h6", "M9 16h3"],
  privacy: ["M12 3l7 3v6c0 4-3 7.5-7 9c-4-1.5-7-5-7-9V6z", "M9 12l2 2l4-4"],
  install: ["M12 4v11", "M7 10l5 5l5-5", "M5 20h14"],
  more: ["M5 12h.01", "M12 12h.01", "M19 12h.01"],
  chevronRight: ["M9 6l6 6l-6 6"],
  chevronLeft: ["M15 6l-6 6l6 6"],
  account: ["M12 12a4 4 0 1 0 0-8a4 4 0 1 0 0 8", "M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"],
  chevronDown: ["M6 9l6 6l6-6"],
  chevronUp: ["M6 15l6-6l6 6"],
  settings: [
    "M12 9a3 3 0 1 0 0 6a3 3 0 1 0 0-6",
    "M12 3v3",
    "M12 18v3",
    "M3 12h3",
    "M18 12h3",
    "M5.6 5.6l2.1 2.1",
    "M16.3 16.3l2.1 2.1",
    "M5.6 18.4l2.1-2.1",
    "M16.3 7.7l2.1-2.1",
  ],
  signOut: ["M10 4H5v16h5", "M14 8l4 4l-4 4", "M9 12h9"],
  roster: ["M4 5h16v15H4z", "M4 9h16", "M8 3v4", "M16 3v4", "M7 13h5", "M12 16h5"],
  rosterPlanning: ["M4 5h16v15H4z", "M4 10h16", "M4 15h16", "M9 5v15", "M14 12h3", "M11 17h4"],
  pause: ["M9 6v12", "M15 6v12"],
  warning: ["M12 4L2.5 20h19z", "M12 10v4", "M12 17h.01"],
};

/** Properties of {@link Icon}. */
export interface IconProps {
  /** The icon to draw. */
  readonly name: IconName;
  /** Optional class name, for example to size the icon. */
  readonly className?: string | undefined;
}

/**
 * Decorative line icon. It is hidden from assistive technology: every control that shows an
 * icon also carries a visible text label (WCAG 1.1.1, 2.5.3).
 *
 * @param props - Component properties.
 * @returns The SVG element.
 */
export function Icon(props: IconProps): JSX.Element {
  const strokeWidth = props.name === "more" || props.name === "pause" ? 3 : 1.75;
  const icon = (
    <svg
      className={props.className}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {iconPaths[props.name].map((path) => (
        <path key={path} d={path} />
      ))}
    </svg>
  );

  return icon;
}
